import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import TicketBadge from '../components/TicketBadge';
import { 
  Calendar, MapPin, Clock, ChevronRight, ChevronLeft, Loader2, ExternalLink,
  Ticket, Users, Tag, Share2, ArrowLeft, DoorOpen, Sparkles,
  Plus, Minus, CheckCircle2, ShoppingCart, Globe, Phone, Star,
  Instagram, Facebook, Youtube, Music2, Gift, Mail, Wallet,
  Image as GalleryIcon,
} from 'lucide-react';
import EventPageBlocks from '../components/EventPageBlocks';
import { LogoSoupRow, SwagBagBadge } from '../components/SwagBagEmbeds';
import { SlotPicker, ReservationPicker } from '../components/BookingPickers';
import EventReviews from '../components/EventReviews';
import SEO from '../components/SEO';
import FollowOrganizerButton from '../components/FollowOrganizerButton';
import { WaitlistSignup } from '../components/WaitlistSignup';
import { TicketsSoonNotify } from '../components/TicketsSoonNotify';
import { cityName, cityState } from '../utils/seoFormat';
import { eventLD, breadcrumbLD, experienceProductLD, reservationVenueLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  party: 'Party', festival: 'Festival', brunch: 'Brunch', competition: 'Competition',
  tasting: 'Tasting', concert: 'Concert', live_music: 'Live Music', networking: 'Networking',
  wine: 'Wine Events', nightlife: 'Nightlife', club: 'Club', comedy: 'Comedy',
  theater: 'Theatre', performing_arts: 'Performing Arts', dance: 'Dance',
  date_night: 'Date Night', food: 'Food', workshop: 'Workshop', class: 'Class',
  wellness: 'Wellness', fitness: 'Fitness', tour: 'Tour', sports: 'Sports',
  gala: 'Gala', fundraiser: 'Fundraiser', charity: 'Charity', conference: 'Conference',
  family: 'Family · Kids', exhibition: 'Exhibition', popup: 'Pop-up', art: 'Art · Gallery',
  film: 'Film · Screening', literary: 'Literary · Book', halloween: 'Halloween',
  nye: "New Year's Eve", holiday: 'Holiday · Seasonal', other: 'Other',
};


// ---- Shared helpers — date / time / price / countdown / scroll-jump ----

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

const fmtTime = (t) => {
  if (!t) return '';
  const [hh, mm] = String(t).split(':');
  const h = parseInt(hh, 10);
  if (Number.isNaN(h)) return t;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${mm || '00'} ${ampm}`;
};

// Friendly seller name derived from an external ticket URL's hostname —
// "Ticketmaster", "AXS", "Etix", … Falls back to the capitalized domain.
const externalSellerName = (url) => {
  try {
    const h = new URL(url).hostname.replace(/^www\./, '');
    const KNOWN = { ticketmaster: 'Ticketmaster', axs: 'AXS', etix: 'Etix', eventbrite: 'Eventbrite', 'dice.fm': 'DICE', seetickets: 'See Tickets', ticketweb: 'TicketWeb', tixr: 'Tixr', stubhub: 'StubHub' };
    for (const k of Object.keys(KNOWN)) if (h.includes(k)) return KNOWN[k];
    const base = h.split('.').slice(-2)[0] || h;
    return base.charAt(0).toUpperCase() + base.slice(1);
  } catch { return 'the seller'; }
};

const priceRangeLabel = (event) => {
  const min = Number(event?.ticket_price_min);
  const max = Number(event?.ticket_price_max);
  // External-link listings carry no Eventism price — never imply "Free".
  if (event?.ticket_type === 'external') {
    if (event.external_price_label) return event.external_price_label;
    if (Number.isFinite(min) && min > 0) {
      return Number.isFinite(max) && max > min ? `$${min}–$${max}` : `from $${min}`;
    }
    return '';
  }
  if (Number.isFinite(min) && min === 0) return 'Free';
  if (Number.isFinite(min) && Number.isFinite(max) && max > min) return `$${min}–$${max}`;
  if (Number.isFinite(min)) return `from $${min}`;
  return '';
};

const countdownLabel = (targetIso) => {
  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  if (Number.isNaN(target)) return null;
  const delta = target - Date.now();
  if (delta <= 0) return null;
  const days = Math.floor(delta / (24 * 60 * 60 * 1000));
  const hours = Math.floor((delta % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const mins = Math.floor((delta % (60 * 60 * 1000)) / (60 * 1000));
  if (days > 1)   return `${days}d ${hours}h left`;
  if (days === 1) return `1 day ${hours}h left`;
  if (hours > 1)  return `${hours}h ${mins}m left`;
  if (hours === 1) return `1h ${mins}m left`;
  if (mins > 0)   return `${mins}m left`;
  return 'Closing now';
};

// Smooth scroll the user to the ticket widget. Reused by both the
// in-page "Quick facts" price chip and the mobile sticky bottom CTA bar.
const scrollToTickets = () => {
  const el = document.getElementById('event-ticket-widget');
  if (!el) return;
  const offset = 80; // global nav + breath
  const y = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: y, behavior: 'smooth' });
};


/**
 * HeroGallery — DesignMyNight-inspired bento gallery.
 *
 * Desktop:
 *   ┌────────────────────────┬──────────────┐
 *   │                        │   image #2   │
 *   │      image #1          ├──────────────┤
 *   │   (spans 2 rows)       │   image #3   │
 *   └────────────────────────┴──────────────┘
 *
 *   - Flyer-friendly: each tile uses `object-cover` over a blurred copy
 *     of itself so portrait flyers and landscape photos both look
 *     intentional.
 *   - "+N more" overlay on the 3rd tile when there are >3 images.
 *
 * Mobile:
 *   Native scroll-snap horizontal carousel at aspect 4:5. Dots indicator
 *   underneath. Single image edge-to-edge (no rounded corners on phone —
 *   more immersive, matches the DMN reference).
 */
function HeroGallery({ images, eventName, onOpenLightbox }) {
  const list = (images || []).filter(Boolean);
  const [activeIdx, setActiveIdx] = useState(0);
  const [deskIdx, setDeskIdx] = useState(0);
  const scrollerRef = useRef(null);

  // Desktop slideshow — gentle auto-rotate when there's more than one image.
  useEffect(() => {
    if (list.length < 2) return undefined;
    const t = setInterval(() => setDeskIdx((i) => (i + 1) % list.length), 5000);
    return () => clearInterval(t);
  }, [list.length]);

  // Mobile carousel: track which image is in view via scroll position.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const idx = Math.round(el.scrollLeft / el.clientWidth);
        setActiveIdx(idx);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  if (list.length === 0) {
    return (
      <div className="aspect-[21/9] max-h-[40vh] bg-gradient-to-br from-fuchsia-500/10 via-amber-500/10 to-indigo-500/10 flex items-center justify-center rounded-none lg:rounded-2xl mx-0 lg:mx-4 max-w-7xl lg:mx-auto">
        <GalleryIcon className="h-12 w-12 text-muted-foreground/40" />
      </div>
    );
  }

  // Single-image case — render one full-bleed image (no empty tile gaps).

  return (
    <>
      {/* Mobile carousel — 4:5 immersive */}
      <div
        ref={scrollerRef}
        className="flex lg:hidden snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden bg-black"
        data-testid="event-hero-carousel-mobile"
      >
        {list.map((src, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onOpenLightbox?.(i)}
            className="snap-start flex-shrink-0 w-full aspect-[4/5] relative overflow-hidden"
            data-testid={`event-hero-image-mobile-${i}`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center blur-2xl opacity-60 scale-110"
              style={{ backgroundImage: `url(${src})` }}
              aria-hidden="true"
            />
            <img
              src={src}
              alt={`${eventName} ${i + 1}`}
              loading={i === 0 ? 'eager' : 'lazy'}
              className="relative w-full h-full object-contain"
            />
          </button>
        ))}
      </div>
      {/* Mobile dots */}
      {list.length > 1 && (
        <div className="flex lg:hidden justify-center gap-1.5 mt-2.5" data-testid="event-hero-dots">
          {list.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === activeIdx ? 'w-6 bg-fuchsia-400' : 'w-1.5 bg-muted'
              }`}
            />
          ))}
        </div>
      )}

      {/* Desktop hero — single full-bleed image (same framing as the
          one-photo layout). With multiple photos it becomes a crossfading
          slideshow with arrows + dots instead of side tiles. */}
      <div
        className="hidden lg:grid grid-cols-1 gap-3 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        data-testid="event-hero-bento"
      >
        <div className="relative rounded-2xl overflow-hidden bg-zinc-900 group aspect-[21/9] max-h-[55vh]" data-testid="event-hero-slideshow">
          {list.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onOpenLightbox?.(i)}
              className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
              style={{ opacity: i === deskIdx ? 1 : 0, pointerEvents: i === deskIdx ? 'auto' : 'none' }}
              data-testid={i === 0 ? 'event-hero-image' : `event-hero-image-${i}`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center blur-2xl opacity-50 scale-110"
                style={{ backgroundImage: `url(${src})` }}
                aria-hidden="true"
              />
              <img
                src={src}
                alt={`${eventName} ${i + 1}`}
                loading={i === 0 ? 'eager' : 'lazy'}
                className="relative w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.02]"
              />
            </button>
          ))}
          {list.length > 1 && (
            <>
              <span className="absolute bottom-3 left-3 z-10 text-[10px] uppercase tracking-widest font-bold bg-black/65 backdrop-blur px-2.5 py-1 rounded-full text-white border border-white/15 pointer-events-none">
                <GalleryIcon className="h-3 w-3 inline mr-1" /> {deskIdx + 1} / {list.length}
              </span>
              <button
                type="button"
                aria-label="Previous photo"
                onClick={(e) => { e.stopPropagation(); setDeskIdx((i) => (i - 1 + list.length) % list.length); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/45 hover:bg-black/70 backdrop-blur flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid="event-hero-prev"
              ><ChevronLeft className="h-5 w-5" /></button>
              <button
                type="button"
                aria-label="Next photo"
                onClick={(e) => { e.stopPropagation(); setDeskIdx((i) => (i + 1) % list.length); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/45 hover:bg-black/70 backdrop-blur flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid="event-hero-next"
              ><ChevronRight className="h-5 w-5" /></button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5 pointer-events-auto">
                {list.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Photo ${i + 1}`}
                    onClick={(e) => { e.stopPropagation(); setDeskIdx(i); }}
                    className={`h-1.5 rounded-full transition-all ${i === deskIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/45 hover:bg-white/70'}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}


/**
 * Lightbox — basic full-screen image viewer triggered from HeroGallery.
 * Keyboard: Esc closes, arrow keys navigate. Click-outside-image closes.
 */
function HeroLightbox({ images, openIdx, onClose }) {
  const [idx, setIdx] = useState(openIdx ?? 0);
  useEffect(() => { if (openIdx !== null) setIdx(openIdx); }, [openIdx]);
  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % images.length);
      if (e.key === 'ArrowLeft')  setIdx((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIdx, images.length, onClose]);
  if (openIdx === null) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="event-hero-lightbox"
    >
      <button
        type="button"
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
        onClick={onClose}
        aria-label="Close"
      >✕</button>
      {images.length > 1 && (
        <>
          <button
            type="button"
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); }}
            aria-label="Previous"
          ><ChevronLeft className="h-6 w-6" /></button>
          <button
            type="button"
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length); }}
            aria-label="Next"
          ><ChevronRight className="h-6 w-6" /></button>
        </>
      )}
      <img
        src={images[idx]}
        alt=""
        className="max-h-full max-w-full object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-xs font-mono tabular-nums">
        {idx + 1} / {images.length}
      </div>
    </div>
  );
}


/**
 * QuickFactsRibbon — horizontal chip row right under the title block.
 * On mobile: 2x2 grid; on desktop: single row. The Price chip is a
 * button that scroll-jumps to the ticket widget.
 */
function QuickFactsRibbon({ event, city, countdown, scarcityBadge }) {
  const priceLabel = priceRangeLabel(event);
  const venueLine = event.venue_name
    ? (event.venue_neighborhood
        ? `${event.venue_name} · ${event.venue_neighborhood}`
        : event.venue_name)
    : null;
  return (
    <div
      className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3"
      data-testid="event-quick-facts"
    >
      {event.event_date && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-card border border-border">
          <Calendar className="h-4 w-4 text-fuchsia-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">When</p>
            <p className="text-xs font-semibold truncate">{fmtDate(event.event_date)}{event.event_time ? ` · ${fmtTime(event.event_time)}` : ''}</p>
          </div>
        </div>
      )}
      {venueLine && event.venue_slug && (
        <Link
          to={`/${city}/venues/${event.venue_slug}`}
          className="flex items-center gap-2 px-3 py-2 rounded-xl bg-card border border-border hover:border-fuchsia-400/40 transition-colors"
          data-testid="venue-profile-link"
        >
          <MapPin className="h-4 w-4 text-rose-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Venue</p>
            <p className="text-xs font-semibold truncate">{venueLine}</p>
          </div>
        </Link>
      )}
      {priceLabel && (
        <button
          type="button"
          onClick={scrollToTickets}
          className="flex items-center gap-2 px-3 py-2 rounded-xl bg-card border border-border hover:border-amber-400/40 transition-colors text-left"
        >
          <Ticket className="h-4 w-4 text-amber-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Tickets</p>
            <p className="text-xs font-semibold truncate">{priceLabel}</p>
          </div>
        </button>
      )}
      {(countdown || scarcityBadge) && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <Clock className="h-4 w-4 text-amber-300 flex-shrink-0 animate-pulse" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
              {scarcityBadge?.text || 'Hurry'}
            </p>
            {countdown && <p className="text-xs font-semibold truncate text-amber-100">{countdown}</p>}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * AtTheVenueCard — rich venue card embedded in the main content column.
 * Photo + neighborhood + map iframe + "See venue profile" link.
 */
function AtTheVenueCard({ event, city }) {
  if (!event.venue_name) return null;
  const venuePhoto = event.venue_image_url || event.venue_photo;
  const mapsQuery = encodeURIComponent(
    `${event.venue_address || ''} ${event.venue_city || ''} ${event.venue_state || ''}`,
  );
  return (
    <section
      className="rounded-2xl overflow-hidden border border-border bg-card"
      data-testid="event-at-the-venue"
    >
      {venuePhoto && (
        <div className="h-48 sm:h-56 w-full overflow-hidden bg-zinc-800">
          <img
            src={venuePhoto}
            alt={event.venue_name}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="p-5 sm:p-6 space-y-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-rose-300 flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5" /> At the venue
          </p>
          {event.venue_slug ? (
            <Link
              to={`/${city}/venues/${event.venue_slug}`}
              className="block mt-1 font-['Space_Grotesk'] text-2xl font-bold hover:text-fuchsia-300 transition-colors"
              data-testid="event-at-the-venue-name"
            >
              {event.venue_name}
            </Link>
          ) : (
            <h3 className="mt-1 font-['Space_Grotesk'] text-2xl font-bold">{event.venue_name}</h3>
          )}
          {event.venue_address && (
            <p className="text-sm text-muted-foreground mt-1">
              {event.venue_address}{event.venue_city ? `, ${event.venue_city}` : ''}{event.venue_state ? `, ${event.venue_state}` : ''}{event.venue_zip ? ` ${event.venue_zip}` : ''}
            </p>
          )}
        </div>
        {(event.venue_lat && event.venue_lng) && (
          <div className="rounded-xl overflow-hidden border border-border aspect-[16/9] max-h-72 bg-card">
            <iframe
              title={`${event.venue_name} on the map`}
              loading="lazy"
              src={`https://www.google.com/maps?q=${event.venue_lat},${event.venue_lng}&z=15&output=embed`}
              className="w-full h-full"
            />
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {event.venue_address && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(`https://maps.google.com/?q=${mapsQuery}`, '_blank')}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Directions
            </Button>
          )}
          {event.venue_slug && (
            <Button
              size="sm"
              className="bg-gradient-to-r from-fuchsia-500 via-amber-500 to-indigo-500 hover:opacity-90 text-white border-0 font-bold"
              asChild
            >
              <Link to={`/${city}/venues/${event.venue_slug}`}>
                See venue profile <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}


/**
 * MoreLikeThisCarousel — horizontal scroller of similar upcoming events
 * (same city, same category, excluding the current event). Lazy-fetched.
 */
function MoreLikeThisCarousel({ city, category, excludeId, kindPrefix }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const url = `${API}/hosted-events?city=${encodeURIComponent(city)}${category ? `&category=${encodeURIComponent(category)}` : ''}&limit=12`;
    axios.get(url)
      .then((r) => { if (!cancel) setItems((r.data?.events || []).filter((e) => e.event_id !== excludeId).slice(0, 8)); })
      .catch(() => { if (!cancel) setItems([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [city, category, excludeId]);

  if (loading || items.length === 0) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: 'smooth' });
  };

  return (
    <section className="mt-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="event-more-like-this">
      <div className="flex items-end justify-between gap-3 mb-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-amber-300 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> More like this
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk'] mt-1">
            You&apos;ll probably like these too
          </h2>
        </div>
        {items.length > 2 && (
          <div className="hidden sm:flex items-center gap-2">
            <Button size="icon" variant="outline" onClick={() => scrollBy(-1)} aria-label="Scroll left"><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="icon" variant="outline" onClick={() => scrollBy(1)}  aria-label="Scroll right"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((e) => (
          <Link
            key={e.event_id}
            to={`${kindPrefix}/${city}/${e.slug || e.event_id}`}
            className="group flex-shrink-0 w-[260px] sm:w-[300px] snap-start rounded-2xl overflow-hidden border border-border hover:border-fuchsia-400/50 transition-colors bg-card"
          >
            <div className="aspect-[4/5] bg-zinc-800 overflow-hidden">
              {e.image_url ? (
                <img src={e.image_url} alt={e.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-fuchsia-500/15 via-amber-500/10 to-indigo-500/15">
                  <Calendar className="h-12 w-12 text-amber-300/40" />
                </div>
              )}
            </div>
            <div className="p-3 space-y-1">
              <p className="text-[10px] uppercase font-bold tracking-widest text-fuchsia-300">
                {fmtDate(e.event_date)}
              </p>
              <p className="font-semibold text-sm leading-snug line-clamp-2 group-hover:text-fuchsia-300 transition-colors">{e.name}</p>
              {e.ticket_price_min != null && !(e.ticket_type === 'external' && !(Number(e.ticket_price_min) > 0)) && (
                <p className="text-xs text-muted-foreground">{Number(e.ticket_price_min) === 0 ? 'Free' : `from $${e.ticket_price_min}`}</p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}


/**
 * MobileBottomCTA — sticky bottom bar pinned to the viewport on mobile.
 * Renders only when the ticket widget exists. Tap → switch active tab
 * to 'tickets' AND scroll the tab nav into view, so the user lands on
 * the ticket type list without an extra scroll.
 */
function MobileBottomCTA({ event, countdown, onTapTickets }) {
  const priceLabel = priceRangeLabel(event);
  const isExternal = (!event.kind || event.kind === 'event') && event.ticket_type === 'external' && !!event.ticket_url;
  if (event.status === 'cancelled') return null;
  if (!priceLabel && !isExternal && event.kind !== 'experience' && event.kind !== 'reservation') return null;
  const ctaText = event.kind === 'experience' ? 'Book Slot'
                 : event.kind === 'reservation' ? 'Reserve'
                 : isExternal ? 'Get Tickets'
                 : Number(event.ticket_price_min) === 0 ? 'RSVP'
                 : 'Get Tickets';
  return (
    <div
      className="fixed bottom-0 inset-x-0 z-50 lg:hidden bg-zinc-950/92 backdrop-blur-xl border-t border-white/10"
      data-testid="mobile-bottom-buy-button"
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          {priceLabel && (
            <p className="text-sm font-bold text-white tabular-nums">{priceLabel}</p>
          )}
          {countdown ? (
            <p className="text-[10px] text-amber-300 font-semibold uppercase tracking-widest truncate">
              {countdown}
            </p>
          ) : (
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest">
              {isExternal ? `Tickets via ${externalSellerName(event.ticket_url)}` : 'Secure checkout'}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={isExternal ? () => window.open(event.ticket_url, '_blank') : onTapTickets}
          className="flex-shrink-0 px-5 py-3 rounded-full bg-gradient-to-r from-fuchsia-500 via-amber-500 to-indigo-500 text-white text-sm font-bold uppercase tracking-widest shadow-[0_0_20px_rgba(217,70,239,0.35)]"
        >
          {ctaText} <ChevronRight className="h-3.5 w-3.5 inline -ml-0.5" />
        </button>
      </div>
    </div>
  );
}


/**
 * EventTabNav — sticky horizontal tab bar that swaps the body section.
 *
 * Unlike the venue page (scrollspy), this is a true tab switcher:
 * exactly one section is mounted at a time. Tabs auto-hide when their
 * underlying data is missing (no ticket types → no Tickets tab;
 * no organizer → no Organiser tab; etc.).
 *
 * Top-right of the nav (desktop only) hosts a persistent gradient
 * "Get Tickets" pill so conversion stays visible without a sidebar.
 */
function EventTabNav({ tabs, active, onChange, ctaLabel, onCtaClick }) {
  const STICKY_TOP = 64;
  return (
    <nav
      className="sticky z-30 bg-background/90 backdrop-blur-md border-b border-border -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 mt-6"
      style={{ top: STICKY_TOP }}
      data-testid="event-tab-nav"
    >
      <div className="flex items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onChange(t.id)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                isActive
                  ? 'text-foreground border-fuchsia-400'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:border-border'
              }`}
              data-testid={`event-tab-${t.id}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
              {t.count ? (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-fuchsia-500/20 text-fuchsia-300' : 'bg-muted text-muted-foreground'
                }`}>{t.count}</span>
              ) : null}
            </button>
          );
        })}
        {/* Spacer pushes the CTA to the right (desktop only). */}
        <div className="flex-1" />
        {ctaLabel && (
          <button
            type="button"
            onClick={onCtaClick}
            className="hidden lg:inline-flex items-center gap-1.5 my-2 px-4 py-2 rounded-full bg-gradient-to-r from-fuchsia-500 via-amber-500 to-indigo-500 text-white text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-opacity"
            data-testid="event-tab-cta"
          >
            <Ticket className="h-3.5 w-3.5" /> {ctaLabel}
          </button>
        )}
      </div>
    </nav>
  );
}


/**
 * PerformersTab — the event lineup. Each performer gets a card with an
 * image gallery (click thumbnails to swap), short details and an
 * Instagram link. Tab only exists when the organizer added performers.
 */
function PerformerCard({ performer }) {
  const [imgIdx, setImgIdx] = useState(0);
  const imgs = performer.images || [];
  return (
    <div className="rounded-xl border border-border overflow-hidden bg-card" data-testid={`performer-card-${performer.performer_id}`}>
      {imgs.length > 0 && (
        <div>
          <img src={imgs[Math.min(imgIdx, imgs.length - 1)]} alt={performer.name}
            className="w-full aspect-[16/10] object-cover" data-testid={`performer-card-img-${performer.performer_id}`} />
          {imgs.length > 1 && (
            <div className="flex gap-1.5 p-2 overflow-x-auto [scrollbar-width:none]">
              {imgs.map((u, i) => (
                <button key={u + i} type="button" onClick={() => setImgIdx(i)}
                  className={`h-12 w-12 rounded-md overflow-hidden flex-shrink-0 border-2 transition-colors ${i === imgIdx ? 'border-fuchsia-400' : 'border-transparent opacity-70 hover:opacity-100'}`}>
                  <img src={u} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="p-4 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-bold">{performer.name}</h3>
          {performer.instagram && (
            <a href={performer.instagram} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-fuchsia-400 hover:text-fuchsia-300 transition-colors"
              data-testid={`performer-card-ig-${performer.performer_id}`}>
              <Instagram className="h-3.5 w-3.5" /> Instagram
            </a>
          )}
        </div>
        {performer.bio && <p className="text-sm text-muted-foreground leading-relaxed">{performer.bio}</p>}
      </div>
    </div>
  );
}

function PerformersTab({ performers }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="event-tab-content-performers">
      {performers.map((p) => <PerformerCard key={p.performer_id || p.name} performer={p} />)}
    </div>
  );
}


/**
 * DescriptionTab — event description with mobile "Read more" toggle.
 * On screens <640px we collapse to ~6 lines and show a fade-out + a
 * "Read more / Read less" button so the page stays scannable.
 */
function DescriptionTab({ event }) {
  const [expanded, setExpanded] = useState(false);
  const desc = (event.description || '').trim();
  if (!desc && !event.highlights?.length) {
    return (
      <p className="text-sm text-muted-foreground py-6">
        The organizer hasn&apos;t published a detailed description yet.
        The basics above are everything we know — check back soon for more.
      </p>
    );
  }
  return (
    <div className="space-y-6" data-testid="event-tab-content-description">
      {desc && (
        <div className="relative">
          <div
            className={`prose prose-invert max-w-none text-foreground/85 leading-relaxed ${
              expanded ? '' : 'max-h-[14rem] sm:max-h-none overflow-hidden'
            }`}
          >
            {desc.split('\n').map((para, idx) => (
              <p key={idx}>{para}</p>
            ))}
          </div>
          {!expanded && (
            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background via-background/85 to-transparent sm:hidden pointer-events-none" />
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-2 text-xs font-bold uppercase tracking-widest text-fuchsia-300 hover:text-fuchsia-200 sm:hidden inline-flex items-center gap-1"
            data-testid="event-description-read-more"
          >
            {expanded ? 'Read less' : 'Read more'} <ChevronRight className={`h-3 w-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>
        </div>
      )}
      {event.highlights?.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-base sm:text-lg font-bold font-['Space_Grotesk'] flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-400" /> What&apos;s included
          </h3>
          <ul className="grid sm:grid-cols-2 gap-2">
            {event.highlights.map((h, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(event.event_time || event.doors_open || event.dress_code || event.age_restriction) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
          {event.event_time && (
            <FactCard icon={Clock} label="Doors / start" value={event.doors_open ? `${fmtTime(event.doors_open)} doors` : fmtTime(event.event_time)} />
          )}
          {event.dress_code && (
            <FactCard icon={Users} label="Dress code" value={event.dress_code} />
          )}
          {event.age_restriction && (
            <FactCard icon={Sparkles} label="Age" value={event.age_restriction} />
          )}
          {event.kind === 'experience' && (
            <FactCard icon={Calendar} label="Format" value="Bookable slot" />
          )}
        </div>
      )}
    </div>
  );
}

function FactCard({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 p-3 rounded-xl bg-card border border-border">
      <Icon className="h-4 w-4 text-fuchsia-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</p>
        <p className="text-xs font-semibold truncate">{value}</p>
      </div>
    </div>
  );
}


/**
 * LocationTab — address + Google Maps embed + "Open in Google Maps" CTA.
 * The map embed gracefully falls back to a "?q=<address>" query when we
 * don't have a precise lat/lng on the event.
 */
function LocationTab({ event, city }) {
  if (!event.venue_name && !event.venue_address) {
    return <p className="text-sm text-muted-foreground py-6">Location to be announced.</p>;
  }
  const addressFull = [event.venue_address, event.venue_city || city, event.venue_state, event.venue_zip]
    .filter(Boolean).join(', ');
  const mapsQuery = encodeURIComponent(addressFull || event.venue_name);
  const embedSrc = (event.venue_lat && event.venue_lng)
    ? `https://www.google.com/maps?q=${event.venue_lat},${event.venue_lng}&z=15&output=embed`
    : `https://www.google.com/maps?q=${mapsQuery}&output=embed`;
  return (
    <div className="space-y-5" data-testid="event-tab-content-location">
      <div>
        {event.venue_slug ? (
          <Link
            to={`/${city}/venues/${event.venue_slug}`}
            className="font-['Space_Grotesk'] text-2xl font-bold hover:text-fuchsia-300 transition-colors"
          >
            {event.venue_name}
          </Link>
        ) : (
          <h3 className="font-['Space_Grotesk'] text-2xl font-bold">{event.venue_name}</h3>
        )}
        {addressFull && (
          <p className="text-sm text-muted-foreground mt-1">{addressFull}</p>
        )}
      </div>
      <div className="rounded-xl overflow-hidden border border-border aspect-[16/9] max-h-[420px] bg-card">
        <iframe
          title={`${event.venue_name} on the map`}
          loading="lazy"
          src={embedSrc}
          className="w-full h-full"
          data-testid="event-location-map"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          className="bg-gradient-to-r from-fuchsia-500 via-amber-500 to-indigo-500 hover:opacity-90 text-white border-0 font-bold"
          onClick={() => window.open(`https://maps.google.com/?q=${mapsQuery}`, '_blank')}
          data-testid="event-open-google-maps"
        >
          <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open in Google Maps
        </Button>
        {event.venue_slug && (
          <Button asChild size="sm" variant="outline">
            <Link to={`/${city}/venues/${event.venue_slug}`}>
              See venue profile <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}


/**
 * OrganiserTab — name, logo, description, website, social links.
 * Backend attaches an `organizer` block via `_attach_organizer` on the
 * by-slug endpoint. When no organizer data is available, the tab is
 * suppressed entirely (see EventTabbedSections tab list builder).
 */
function OrganiserTab({ organizer, coOrganizers = [] }) {
  if (!organizer) return null;
  return (
    <div className="space-y-4" data-testid="event-tab-content-organiser">
      {coOrganizers.length > 0 && (
        <div className="rounded-xl border border-border p-4" data-testid="event-co-organizers">
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-2">Co-organized by</p>
          <div className="flex flex-wrap gap-2">
            {coOrganizers.map((c) => c.org_slug ? (
              <Link key={c.user_id} to={`/organizations/${c.org_slug}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm font-semibold hover:bg-amber-500/20 transition-colors"
                data-testid={`co-organizer-${c.user_id}`}>
                {c.name}
              </Link>
            ) : (
              <span key={c.user_id} className="inline-flex items-center px-3 py-1.5 rounded-full border border-border text-sm font-semibold" data-testid={`co-organizer-${c.user_id}`}>
                {c.name}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-start gap-4">
        {organizer.logo_url ? (
          <div className="h-16 w-16 rounded-2xl border border-border bg-card flex items-center justify-center flex-shrink-0 overflow-hidden">
            <img
              src={organizer.logo_url}
              alt={organizer.name}
              className="max-h-full max-w-full object-contain p-1"
            />
          </div>
        ) : (
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-fuchsia-500/20 via-amber-500/15 to-indigo-500/20 flex items-center justify-center flex-shrink-0">
            <Users className="h-8 w-8 text-fuchsia-300" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-bold flex items-center gap-2">
            {organizer.name}
            {organizer.verified && (
              <CheckCircle2 className="h-4 w-4 text-blue-400" title="Verified organizer" />
            )}
          </h3>
          {organizer.location && (
            <p className="text-xs text-muted-foreground mt-0.5">{organizer.location}</p>
          )}
        </div>
      </div>
      {organizer.description && (
        <p className="text-sm text-foreground/85 whitespace-pre-line leading-relaxed">
          {organizer.description}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {organizer.slug && (
          <Button asChild size="sm" className="bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white hover:opacity-90">
            <Link to={`/organizations/${organizer.slug}?message=1`} data-testid="event-organiser-message">
              <Mail className="h-3.5 w-3.5 mr-1.5" /> Message organizer
            </Link>
          </Button>
        )}
        {organizer.slug && <FollowOrganizerButton slug={organizer.slug} />}
        {organizer.website && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open(/^https?:\/\//i.test(organizer.website) ? organizer.website : `https://${organizer.website}`, '_blank', 'noopener,noreferrer')}
            data-testid="event-organiser-website"
          >
            <Globe className="h-3.5 w-3.5 mr-1.5" /> Visit website
          </Button>
        )}
        {organizer.slug && (
          <Button asChild size="sm" variant="outline">
            <Link to={`/organizations/${organizer.slug}`}>
              See all events <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Link>
          </Button>
        )}
      </div>

      {/* Social icons — one anchor per non-empty social handle. Handles
          may arrive as either bare handles ('@denvertaproom') or full
          URLs; normalize both. Only render icons for known networks so
          unknown keys can't spawn broken links. */}
      {organizer.social_links && Object.keys(organizer.social_links).length > 0 && (
        <div
          className="flex flex-wrap gap-2 pt-1"
          data-testid="event-organiser-socials"
        >
          {SOCIAL_META.map(({ key, Icon, base, label }) => {
            const raw = (organizer.social_links[key] || '').trim();
            if (!raw) return null;
            const href = /^https?:\/\//i.test(raw)
              ? raw
              : `${base}${raw.replace(/^@/, '')}`;
            return (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${label} — opens in new tab`}
                className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-border/60 bg-card/40 text-muted-foreground hover:text-fuchsia-300 hover:border-fuchsia-500/60 transition-colors"
                data-testid={`event-organiser-social-${key}`}
              >
                <Icon className="h-4 w-4" />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}


// Map social_links keys → { Icon component, URL base for bare handles,
// human label for aria-label }. Kept adjacent to the OrganiserTab
// consumer so it's obvious what happens when a new network is added
// to `_attach_organizer` on the backend.
const SOCIAL_META = [
  { key: 'instagram', Icon: Instagram, base: 'https://instagram.com/',     label: 'Instagram' },
  { key: 'facebook',  Icon: Facebook,  base: 'https://facebook.com/',      label: 'Facebook'  },
  { key: 'youtube',   Icon: Youtube,   base: 'https://youtube.com/@',      label: 'YouTube'   },
  // Twitter/X — lucide has a legacy Twitter icon; use Globe as neutral fallback
  { key: 'twitter',   Icon: Globe,     base: 'https://x.com/',             label: 'Twitter'   },
  // TikTok — no lucide icon so fall back to Music2 as an audio-first hint
  { key: 'tiktok',    Icon: Music2,    base: 'https://tiktok.com/@',       label: 'TikTok'    },
];


/**
 * EventTabSections — the orchestrator. Builds the tab list from the
 * data on hand (suppressing tabs whose data is missing), renders the
 * sticky tab nav + the active section. The Tickets tab body is passed
 * in as a render-prop so the ~200-line ticket selection / SlotPicker /
 * checkout JSX stays in the main component without abstraction overhead.
 */
function EventTabSections({ event, city, activeTab, setActiveTab, tabNavRef, renderTicketsTab, isDesktop }) {
  // Build conditional tab list. Order matters — Description first
  // (default), Tickets next (so it's always nearby for conversion),
  // Location, then Organiser. Tab label varies by `event.kind` so an
  // experience reads "Experience Tickets" and a reservation reads
  // "Reservations" — gives the reader the right mental model up front.
  const tabs = React.useMemo(() => {
    const t = [
      { id: 'description', label: 'Description', icon: GalleryIcon },
    ];
    if (event.kind === 'experience' || event.kind === 'reservation'
        || (event.ticket_type && event.ticket_type !== 'none')) {
      const ticketLabel = event.kind === 'experience' ? 'Experience Tickets'
                         : event.kind === 'reservation' ? 'Reservations'
                         : 'Tickets';
      t.push({ id: 'tickets', label: ticketLabel, icon: Ticket });
    }
    if ((event.performers || []).length > 0) {
      t.push({ id: 'performers', label: 'Performers', icon: Music2, count: event.performers.length });
    }
    if (event.venue_name || event.venue_address) {
      t.push({ id: 'location', label: 'Location', icon: MapPin });
    }
    if (event.organizer || (event.co_organizers || []).length > 0) {
      t.push({ id: 'organiser', label: 'Organiser', icon: Users });
    }
    return t;
  }, [event]);

  // If the user lands on a tab that gets filtered out (rare), reset.
  React.useEffect(() => {
    if (!tabs.find((t) => t.id === activeTab)) {
      setActiveTab(tabs[0]?.id || 'description');
    }
  }, [tabs, activeTab, setActiveTab]);

  const hasTicketsTab = tabs.some((t) => t.id === 'tickets');
  const ctaLabel = hasTicketsTab
    ? (event.kind === 'experience' ? 'Book Slot'
       : event.kind === 'reservation' ? 'Reserve'
       : event.ticket_type === 'external' ? 'Get Tickets'
       : Number(event.ticket_price_min) === 0 ? 'RSVP' : 'Get Tickets')
    : null;

  return (
    <div ref={tabNavRef}>
      <EventTabNav
        tabs={tabs}
        active={activeTab}
        onChange={setActiveTab}
        ctaLabel={ctaLabel}
        onCtaClick={() => setActiveTab('tickets')}
      />
      <div className="pt-6 pb-2 max-w-4xl">
        {activeTab === 'description' && <DescriptionTab event={event} />}
        {activeTab === 'tickets' && renderTicketsTab()}
        {activeTab === 'performers' && <PerformersTab performers={event.performers || []} />}
        {activeTab === 'location'    && <LocationTab event={event} city={city} />}
        {activeTab === 'organiser'   && <OrganiserTab organizer={event.organizer} coOrganizers={event.co_organizers || []} />}
      </div>
    </div>
  );
}


// Build the price-range + countdown helpers caller would expect later.
// (Local-only — exported nothing.)
export default function EventDetailPage() {
  const { city, slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [event, setEvent] = useState(null);
  // Review aggregate + recent items, pulled by <EventReviews> and surfaced
  // to JSON-LD so Google picks up `aggregateRating` + `review[]`.
  const [reviewsData, setReviewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  
  // Ticket purchase state
  const [ticketTypes, setTicketTypes] = useState([]);
  const [ticketSelections, setTicketSelections] = useState({});
  const [addonSelections, setAddonSelections] = useState({});
  const [buyerInfo, setBuyerInfo] = useState({ name: '', email: '', phone: '' });
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);
  const [discountChecking, setDiscountChecking] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [walletBalanceCents, setWalletBalanceCents] = useState(0);
  const [useWalletCredit, setUseWalletCredit] = useState(false);
  const [pageBuilder, setPageBuilder] = useState(null);
  const [activePromoCode, setActivePromoCode] = useState('');
  const [feeConfig, setFeeConfig] = useState(null);
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState(null);
  const [occurrenceAvailability, setOccurrenceAvailability] = useState({});
  // Hero lightbox — `null` = closed, otherwise the index to open at.
  const [lightboxIdx, setLightboxIdx] = useState(null);
  // Active tab on the new DesignMyNight-style tab bar — Description is
  // the default landing tab.
  const [activeTab, setActiveTab] = useState('description');
  const tabNavRef = useRef(null);
  // Viewport detection — drives whether the booking widget renders in
  // the sticky right sidebar (desktop) or inside the Tickets tab body
  // (mobile). Conditional rendering (NOT just CSS) so SlotPicker /
  // qty pickers / etc. don't double-mount with diverging state.
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(min-width: 1024px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const switchToTickets = useCallback(() => {
    setActiveTab('tickets');
    // Wait a tick so React renders the new tab content before we scroll.
    requestAnimationFrame(() => {
      tabNavRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);
  // Re-render countdown timer every 60s. Cheap and the urgency is
  // human-readable (days/hours) so per-second updates would be noise.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    fetchEvent();
    // Capture promo code from URL into 30-day cookie + localStorage
    try {
      const url = new URL(window.location.href);
      const code = url.searchParams.get('p');
      if (code) {
        const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `eventism_promo=${encodeURIComponent(code)}; expires=${expires}; path=/; SameSite=Lax`;
        localStorage.setItem('eventism_promo', JSON.stringify({ code, ts: Date.now() }));
        setActivePromoCode(code);
      } else {
        // Fallback to existing cookie/localStorage
        const m = document.cookie.match(/(?:^|;\s*)eventism_promo=([^;]+)/);
        if (m) setActivePromoCode(decodeURIComponent(m[1]));
      }
    } catch { /* ignore */ }
  }, [city, slug]);

  // Canonical-URL redirect: if a user lands on /events/.. for an
  // experience or reservation (legacy share link, e.g.), rewrite to the
  // kind-specific silo `/ex/..` or `/res/..` while preserving query
  // params. Replaces history so the back-button still works naturally.
  useEffect(() => {
    if (!event?.kind) return;
    const expectedPrefix = event.kind === 'experience'
      ? '/ex'
      : (event.kind === 'reservation' ? '/res' : '/events');
    const currentPrefix = location.pathname.split('/')[1];
    const expected = expectedPrefix.replace(/^\//, '');
    if (currentPrefix && currentPrefix !== expected) {
      navigate(`${expectedPrefix}/${event.city || city}/${event.slug || slug}${location.search || ''}`, { replace: true });
    }
  }, [event?.kind, event?.city, event?.slug, location.pathname, location.search, city, slug, navigate]);

  // Track event page view for analytics
  useEffect(() => {
    if (!event?.event_id) return;
    // Persistent visitor identity — survives across visits so Unique
    // Visitors counts PEOPLE, not page loads.
    let visitorId = '';
    try {
      visitorId = localStorage.getItem('eventism_vid') || '';
      if (!visitorId) {
        visitorId = `vis_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem('eventism_vid', visitorId);
      }
    } catch { visitorId = `vis_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`; }
    // Per-browser-session id — stable across refreshes in the same tab session
    let sessionId = '';
    try {
      sessionId = sessionStorage.getItem('eventism_sid') || '';
      if (!sessionId) {
        sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        sessionStorage.setItem('eventism_sid', sessionId);
      }
    } catch { sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; }
    const enteredAt = Date.now();

    // Capture UTMs from the URL (set by Share & Track preset links).
    // Persist into sessionStorage so the click-to-purchase chain keeps
    // the attribution even if the buyer navigates within the page.
    let utm = {};
    try {
      const sp = new URL(window.location.href).searchParams;
      utm = {
        utm_source:   sp.get('utm_source')   || undefined,
        utm_medium:   sp.get('utm_medium')   || undefined,
        utm_campaign: sp.get('utm_campaign') || undefined,
        utm_content:  sp.get('utm_content')  || undefined,
        utm_id:       sp.get('utm_id')       || undefined,
      };
      const hasAny = Object.values(utm).some(Boolean);
      if (hasAny) {
        // localStorage (7 days) so attribution survives new tabs/sessions —
        // buyers often return a day later to complete the purchase.
        localStorage.setItem(
          `eventism_utm_${event.event_id}`,
          JSON.stringify({ ...utm, ts: Date.now() }),
        );
      } else {
        // Fall back to a stored UTM tuple (same event, within 7 days)
        const stored = localStorage.getItem(`eventism_utm_${event.event_id}`) || sessionStorage.getItem(`eventism_utm_${event.event_id}`);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (!parsed.ts || Date.now() - parsed.ts < 7 * 864e5) utm = parsed;
          } catch { /* ignore */ }
        }
      }
    } catch { /* ignore */ }

    // Editorial attribution — if the visitor arrived from an Eventism blog
    // post or city guide, remember it so the purchase can credit the article.
    try {
      const ref = document.referrer || '';
      if (ref) {
        const ru = new URL(ref);
        if (ru.origin === window.location.origin) {
          const parts = ru.pathname.split('/').filter(Boolean);
          const isEditorial = (parts.length === 2 && parts[0] === 'blog') || (parts.length === 3 && parts[1] === 'guides');
          if (isEditorial) sessionStorage.setItem(`eventism_edref_${event.event_id}`, ru.pathname);
        }
      }
    } catch { /* ignore */ }

    // Auto-apply discount link (?discount=CODE)
    try {
      const dc = (new URLSearchParams(window.location.search).get('discount') || '').toUpperCase();
      if (dc) { setDiscountCode(dc); applyDiscountCode(dc); }
    } catch { /* ignore */ }
    // Kickback referral capture (?ref=CODE) — 7-day credit window
    try {
      const ref = (new URLSearchParams(window.location.search).get('ref') || '').trim().toLowerCase();
      if (ref) {
        localStorage.setItem(`eventism_kbref_${event.event_id}`, JSON.stringify({ code: ref, exp: Date.now() + 7 * 24 * 60 * 60 * 1000 }));
      }
    } catch { /* ignore */ }
    // Geo lookup — cache once per browser session so we don't hammer
    // ipapi.co for every page view. Without this, the backend
    // `event_views.geo` stays empty and the Top States / Top Cities
    // analytics blocks never populate.
    const cachedGeoRaw = sessionStorage.getItem('eventism_geo');
    let geo = null;
    try { geo = cachedGeoRaw ? JSON.parse(cachedGeoRaw) : null; } catch { /* ignore */ }

    const sendTrack = (geoPayload) => {
      // Send auth when present so the backend can EXCLUDE internal team
      // visits (admin/organizer browsing their own page) from analytics.
      const authToken = (() => { try { return localStorage.getItem('eventism_token'); } catch { return null; } })();
      fetch(`${API}/events/track-view`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          event_id: event.event_id,
          session_id: sessionId,
          visitor_id: visitorId,
          path: window.location.pathname,
          referrer: document.referrer,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          screen_width: window.innerWidth,
          utm_source:   utm.utm_source,
          utm_medium:   utm.utm_medium,
          utm_campaign: utm.utm_campaign,
          utm_content:  utm.utm_content,
          utm_id:       utm.utm_id,
          promo_code: (() => {
            try {
              const m = document.cookie.match(/(?:^|;\s*)eventism_promo=([^;]+)/);
              if (m) return decodeURIComponent(m[1]);
              const ls = JSON.parse(localStorage.getItem('eventism_promo') || 'null');
              if (ls?.code && Date.now() - (ls.ts || 0) < 30 * 24 * 60 * 60 * 1000) return ls.code;
            } catch { /* ignore */ }
            return undefined;
          })(),
          city:    geoPayload?.city || '',
          state:   geoPayload?.state || '',
          country: geoPayload?.country || 'US',
        }),
      }).catch(() => {});
    };

    if (geo && geo.city) {
      sendTrack(geo);
    } else {
      // Best-effort geo enrichment. Always fire the track-view even if
      // the lookup fails so we never lose the raw page-view count.
      fetch('https://ipapi.co/json/', { cache: 'force-cache' })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => {
          if (data && data.city) {
            const enriched = {
              city: data.city,
              state: data.region_code || data.region || '',
              country: data.country_code || 'US',
            };
            try { sessionStorage.setItem('eventism_geo', JSON.stringify(enriched)); } catch { /* ignore */ }
            sendTrack(enriched);
          } else {
            sendTrack(null);
          }
        })
        .catch(() => sendTrack(null));
    }

    // Track exit / duration
    const trackExit = () => {
      const duration = Math.round((Date.now() - enteredAt) / 1000);
      navigator.sendBeacon?.(
        `${API}/events/track-exit`,
        JSON.stringify({ session_id: sessionId, event_id: event.event_id, duration_seconds: duration })
      );
    };
    window.addEventListener('beforeunload', trackExit);
    return () => {
      trackExit();
      window.removeEventListener('beforeunload', trackExit);
    };
  }, [event?.event_id]);

  const fetchEvent = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const previewToken = params.get('preview');
      // ?preview=1 (or any falsy/non-token value) means "owner preview" —
      // send the user's bearer token so the backend can let admins/orgs
      // view their own drafts. A real proposal token is still passed
      // through as a query string for venue-side previews.
      const isOwnerPreview = previewToken === '1' || previewToken === 'true';
      const url = previewToken && !isOwnerPreview
        ? `${API}/hosted-events/by-slug/${city}/${slug}?preview=${encodeURIComponent(previewToken)}`
        : `${API}/hosted-events/by-slug/${city}/${slug}`;
      const headers = {};
      if (isOwnerPreview) {
        const tok = localStorage.getItem('eventism_token');
        if (tok) headers.Authorization = `Bearer ${tok}`;
      }
      const response = await axios.get(url, { headers });
      setEvent(response.data);
      // Fetch ticket types if internal ticketing
      if (response.data?.ticket_type === 'internal') {
        const ttRes = await axios.get(`${API}/events/${response.data.event_id}/ticket-types`);
        setTicketTypes((ttRes.data.ticket_types || []).filter(tt => tt.is_active));
        try {
          const fRes = await axios.get(`${API}/events/${response.data.event_id}/ticket-fees/public`);
          setFeeConfig(fRes.data);
        } catch { /* fee config optional */ }
        // Multi-day: fetch per-occurrence availability so we can show "Sold out" badges
        if ((response.data?.occurrences || []).length > 0) {
          try {
            const oa = await axios.get(`${API}/events/${response.data.event_id}/occurrence-availability`);
            const map = {};
            (oa.data?.occurrences || []).forEach(o => { map[o.occurrence_id] = o; });
            setOccurrenceAvailability(map);
          } catch { /* optional */ }
        }
      }
      // Fetch page-builder blocks (gated by `enabled` flag)
      try {
        const pbRes = await axios.get(`${API}/events/${response.data.event_id}/page-builder`);
        setPageBuilder(pbRes.data);
      } catch { /* no page builder */ }
    } catch (err) {
      console.error('Failed to fetch event:', err);
      setError('Event not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const updateTicketQty = (ttId, delta) => {
    setTicketSelections(prev => {
      const current = prev[ttId] || 0;
      const tt = ticketTypes.find(t => t.ticket_type_id === ttId);
      const minQty = Math.max(1, tt?.min_per_order || 1);
      const max = Math.min(tt?.max_per_order || 10, (tt?.quantity_total || 0) - (tt?.quantity_sold || 0));
      let next = current + delta;
      // Snap to minimum on first +tap so buyer doesn't have to mash "+"
      // for a "minimum 5" tier; -tap below min drops to zero.
      if (delta > 0 && current === 0 && minQty > 1) next = Math.min(max, minQty);
      if (delta < 0 && next > 0 && next < minQty) next = 0;
      const newVal = Math.max(0, Math.min(max, next));
      return { ...prev, [ttId]: newVal };
    });
  };

  const totalTickets = Object.values(ticketSelections).reduce((s, v) => s + v, 0);

  // ---- Add-ons (Eventbrite-style) ----
  const regularTypes = ticketTypes.filter(tt => !tt.is_addon);
  const addonTypes = ticketTypes.filter(tt => tt.is_addon);
  // One purchasable row per variation (or one row for simple add-ons)
  const addonRows = addonTypes.flatMap(tt =>
    (tt.variations || []).length > 0
      ? tt.variations.map(v => ({ key: `${tt.ticket_type_id}::${v.id}`, tt, variation: v, price: v.price }))
      : [{ key: tt.ticket_type_id, tt, variation: null, price: tt.price }]
  );
  // Tickets in the cart this add-on applies to (empty list = all ticket types)
  const addonEligibleTickets = (tt) => {
    const allowed = tt.applies_to_ticket_types || [];
    if (allowed.length === 0) return totalTickets;
    return allowed.reduce((s, id) => s + (ticketSelections[id] || 0), 0);
  };
  const updateAddonQty = (row, delta) => {
    setAddonSelections(prev => {
      const current = prev[row.key] || 0;
      const available = (row.tt.quantity_total || 0) - (row.tt.quantity_sold || 0);
      // Shared inventory across variations of the same add-on
      const siblingQty = Object.entries(prev).reduce((s, [k, q]) =>
        k !== row.key && k.startsWith(row.tt.ticket_type_id) ? s + q : s, 0);
      const eligible = addonEligibleTickets(row.tt);
      // Always allow at least one add-on per eligible ticket in the order
      const perOrderCap = Math.max(row.tt.max_per_order || 10, eligible);
      const max = Math.max(0, Math.min(perOrderCap, available - siblingQty));
      // First "Add" defaults to one upgrade per ticket — buyers can step back down
      const next = current === 0 && delta > 0 ? Math.max(1, Math.min(eligible, max)) : current + delta;
      return { ...prev, [row.key]: Math.max(0, Math.min(max, next)) };
    });
  };
  const totalAddonUnits = Object.values(addonSelections).reduce((s, v) => s + v, 0);
  const addonPrice = addonRows.reduce((sum, row) => sum + row.price * (addonSelections[row.key] || 0), 0);

  // Add-ons only make sense alongside tickets — clear picks when the last ticket
  // is removed, and drop restricted add-ons when their eligible tickets are gone
  useEffect(() => {
    setAddonSelections(prev => {
      if (!Object.keys(prev).length) return prev;
      if (totalTickets === 0) return {};
      const next = { ...prev };
      let changed = false;
      for (const key of Object.keys(next)) {
        const tt = addonTypes.find(t => key.startsWith(t.ticket_type_id));
        if (tt && (tt.applies_to_ticket_types || []).length > 0 && addonEligibleTickets(tt) === 0) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketSelections]);

  const totalPrice = ticketTypes.reduce((sum, tt) => sum + (tt.price * (ticketSelections[tt.ticket_type_id] || 0)), 0) + addonPrice;
  const discountAmount = discountInfo ? ticketTypes.reduce((sum, tt) => {
    const qty = ticketSelections[tt.ticket_type_id] || 0;
    const row = (discountInfo.items || []).find(i => i.ticket_type_id === tt.ticket_type_id && i.eligible);
    return sum + (row && qty ? (row.price - row.discounted_price) * qty : 0);
  }, 0) : 0;
  const discountedSubtotal = Math.max(0, totalPrice - discountAmount);
  // Industry-standard service fee (3% + $1.50/ticket default), only added when buyer absorbs.
  const buyerFee = (() => {
    if (!feeConfig?.buyer_pays_fee || discountedSubtotal === 0 || (totalTickets + totalAddonUnits) === 0) return 0;
    const pct = parseFloat(feeConfig.percent || 0) + parseFloat(feeConfig.payment_processing_percent || 0);
    const flat = parseInt(feeConfig.flat_cents || 0, 10) / 100;
    return Math.round((discountedSubtotal * (pct / 100) + flat * (totalTickets + totalAddonUnits)) * 100) / 100;
  })();
  const buyerTotal = discountedSubtotal + buyerFee;
  // Wallet credit math — applied amount mirrors the backend (leave ≥ $0.50 on card for partial)
  const walletApplied = (() => {
    if (!useWalletCredit || walletBalanceCents <= 0 || buyerTotal <= 0) return 0;
    const totalCents = Math.round(buyerTotal * 100);
    let apply = Math.min(walletBalanceCents, totalCents);
    if (apply < totalCents && totalCents - apply < 50) apply = totalCents - 50;
    return apply / 100;
  })();
  const buyerDue = Math.max(0, Math.round((buyerTotal - walletApplied) * 100) / 100);

  useEffect(() => {
    try {
      const tok = localStorage.getItem('eventism_token');
      if (!tok) return;
      axios.get(`${API}/me/money`, { headers: { Authorization: `Bearer ${tok}` } })
        .then(r => setWalletBalanceCents(r.data.balance_cents || 0)).catch(() => {});
    } catch { /* ignore */ }
  }, []);

  // Keep discount math in sync with the live selection — re-validate whenever
  // quantities change while a code is applied (fixes stale $0-discount state).
  useEffect(() => {
    if (!discountInfo || !event?.event_id) return;
    const its = Object.entries(ticketSelections).filter(([, q]) => q > 0).map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity }));
    axios.post(`${API}/events/${event.event_id}/discounts/validate`, { code: discountInfo.code, items: its })
      .then(res => setDiscountInfo(res.data))
      .catch(() => setDiscountInfo(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketSelections]);

  const applyDiscountCode = async (codeArg) => {
    const code = (codeArg || discountCode || '').trim().toUpperCase();
    if (!code) return;
    setDiscountChecking(true);
    try {
      const its = Object.entries(ticketSelections).filter(([, q]) => q > 0).map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity }));
      const res = await axios.post(`${API}/events/${event.event_id}/discounts/validate`, { code, items: its });
      setDiscountInfo(res.data);
      setDiscountCode(code);
      toast.success(`Code ${code} applied — ${res.data.label}`);
    } catch (e) {
      setDiscountInfo(null);
      toast.error(e.response?.data?.detail || 'Invalid discount code');
    } finally { setDiscountChecking(false); }
  };

  const handlePurchase = async () => {
    if (!buyerInfo.name || !buyerInfo.email) {
      toast.error('Please enter your name and email');
      return;
    }
    const items = Object.entries(ticketSelections)
      .filter(([_, qty]) => qty > 0)
      .map(([ttId, qty]) => {
        const tt = ticketTypes.find(t => t.ticket_type_id === ttId);
        // For multi-day events: ticket type's own occurrence_id wins; otherwise use buyer's chosen day
        const occ = tt?.occurrence_id || selectedOccurrenceId || null;
        return { ticket_type_id: ttId, quantity: qty, occurrence_id: occ };
      });
    if (items.length === 0) {
      toast.error(totalAddonUnits > 0 ? 'Add at least one ticket to purchase add-ons' : 'Please select at least one ticket');
      return;
    }
    // Add-on line items ({ticket_type_id}::{variation_id} key scheme)
    Object.entries(addonSelections).filter(([_, qty]) => qty > 0).forEach(([key, qty]) => {
      const [ttId, varId] = key.split('::');
      items.push({ ticket_type_id: ttId, quantity: qty, variation_id: varId || undefined });
    });
    // Multi-day events: require an occurrence pick when not all selected types are universal
    if ((event.occurrences || []).length > 0 && items.some(it => !it.variation_id && !it.occurrence_id && ticketSelections[it.ticket_type_id] > 0)) {
      toast.error('Please pick which day you are attending');
      return;
    }

    setPurchasing(true);
    try {
      // Pull promo code from URL first, then cookie/localStorage (30-day attribution)
      const url = new URL(window.location.href);
      let promoCode = url.searchParams.get('p') || '';
      if (!promoCode) {
        const m = document.cookie.match(/(?:^|;\s*)eventism_promo=([^;]+)/);
        if (m) promoCode = decodeURIComponent(m[1]);
      }
      if (!promoCode) {
        try { const ls = JSON.parse(localStorage.getItem('eventism_promo') || 'null'); if (ls?.code && Date.now() - (ls.ts || 0) < 30 * 24 * 60 * 60 * 1000) promoCode = ls.code; } catch { /* ignore */ }
      }
      // Pull UTMs from the URL first, then fall back to the
      // sessionStorage tuple stashed by track-view (handles the case
      // where the buyer arrived via a Share & Track preset and then
      // bounced between tabs before completing checkout).
      let utm = {};
      try {
        const sp = url.searchParams;
        utm = {
          utm_source:   sp.get('utm_source')   || undefined,
          utm_medium:   sp.get('utm_medium')   || undefined,
          utm_campaign: sp.get('utm_campaign') || undefined,
          utm_content:  sp.get('utm_content')  || undefined,
          utm_id:       sp.get('utm_id')       || undefined,
        };
        if (!Object.values(utm).some(Boolean)) {
          const stored = localStorage.getItem(`eventism_utm_${event.event_id}`) || sessionStorage.getItem(`eventism_utm_${event.event_id}`);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              if (!parsed.ts || Date.now() - parsed.ts < 7 * 864e5) utm = parsed;
            } catch { /* ignore */ }
          }
        }
      } catch { /* ignore */ }
      const hasUtm = Object.values(utm).some(Boolean);
      let editorialRef;
      try { editorialRef = sessionStorage.getItem(`eventism_edref_${event.event_id}`) || undefined; } catch { /* ignore */ }
      let kickbackRef;
      try {
        const raw = localStorage.getItem(`eventism_kbref_${event.event_id}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed.exp > Date.now()) kickbackRef = parsed.code;
          else localStorage.removeItem(`eventism_kbref_${event.event_id}`);
        }
      } catch { /* ignore */ }

      const walletTok = (() => { try { return localStorage.getItem('eventism_token'); } catch { return null; } })();
      const res = await axios.post(`${API}/events/${event.event_id}/purchase`, {
        name: buyerInfo.name,
        email: buyerInfo.email,
        phone: buyerInfo.phone,
        items,
        use_wallet: useWalletCredit && walletBalanceCents > 0,
        promo_code: promoCode || undefined,
        discount_code: discountInfo ? discountInfo.code : undefined,
        kickback_ref: kickbackRef,
        editorial_ref: editorialRef,
        // Order source — "venue_experiences" when the buyer clicked an
        // experience card on a venue page (?src=venue_experiences).
        purchase_source: new URLSearchParams(window.location.search).get('src') === 'venue_experiences' ? 'venue_experiences' : 'event_page',
        // Attribution passthrough — when the buyer arrived via a Share
        // & Track preset link, forward the UTMs so the Stripe webhook
        // can write a `widget_views` conversion row and close the loop
        // for the "Top Channel" / Widget Performance dashboards.
        ...(hasUtm && {
          embed_source: 'page',
          embed_host:   'eventism.com',
          embed_utm_source:   utm.utm_source,
          embed_utm_medium:   utm.utm_medium,
          embed_utm_campaign: utm.utm_campaign,
          embed_utm_content:  utm.utm_content,
          embed_utm_id:       utm.utm_id,
        }),
      }, useWalletCredit && walletTok ? { headers: { Authorization: `Bearer ${walletTok}` } } : undefined);
      if (res.data.free || res.data.success || res.data.paid_with_wallet) {
        navigate(`/tickets/confirmation?order_id=${res.data.order_id}`);
      } else if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.name,
          text: event.tagline || event.short_description,
          url: window.location.href
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <SEO
          title="Event Not Found"
          description="This event could not be found. Browse all upcoming events on Eventism."
          noindex
        />
        <Card className="max-w-md w-full mx-4 text-center">
          <CardContent className="pt-8">
            <Calendar className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Event Not Found</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/events')}>Browse All Events</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allImages = event.gallery_images?.length > 0 
    ? [event.image_url, ...event.gallery_images].filter(Boolean)
    : event.image_url ? [event.image_url] : [];

  // === HTML Page (full takeover) ===
  // If the event has a visible `html_page` block, render only that — it replaces
  // the entire event page. Optional toggles hide the global nav / footer and
  // show a floating "Buy Tickets" button.
  const htmlBlock = (pageBuilder?.blocks || []).find(b => b?.type === 'html_page' && b?.is_visible !== false);
  if (htmlBlock) {
    return <HtmlPageTakeover block={htmlBlock} event={event} />;
  }

  // ---- SEO setup ---------------------------------------------------------
  const evCity = event.city || city;
  const cityDisplay = cityName(evCity);
  const state = cityState(evCity);
  const legacyVenue = typeof event.venue === 'object' ? event.venue?.name : event.venue;
  const venueName = event.venue_name
    || ((legacyVenue && String(legacyVenue).trim().toLowerCase() !== 'tba') ? legacyVenue : '');
  const evDate = event.start_datetime || event.event_date || event.occurrences?.[0]?.start_datetime;
  // Date-only strings must be parsed as LOCAL midnight; new Date('YYYY-MM-DD')
  // parses as UTC and renders the previous day in US timezones.
  const evDateObj = evDate
    ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(evDate) ? `${evDate}T00:00:00` : evDate)
    : null;
  const dateStr = evDateObj ? evDateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';
  const seoTitle = `${event.name || event.title}${dateStr ? ' — ' + dateStr : ''}${cityDisplay ? ' · ' + cityDisplay : ''}${state ? ', ' + state : ''}`;
  const seoDescription = (
    event.short_description ||
    event.description ||
    `Get tickets for ${event.name} in ${cityDisplay}${venueName ? ' at ' + venueName : ''}. Powered by Eventism — secure checkout, instant digital tickets, and exclusive perks.`
  ).slice(0, 300);
  const seoKeywords = [
    event.name,
    `${event.name} tickets`,
    `${event.name} ${cityDisplay}`,
    `${cityDisplay} events`,
    `${cityDisplay} ${(event.category || 'party').toLowerCase()}`,
    `things to do in ${cityDisplay}`,
    `${cityDisplay} nightlife`,
    venueName ? `events at ${venueName}` : '',
    state ? `${state} events` : '',
    'Eventism tickets',
  ].filter(Boolean);
  // ---- Kind-aware URL prefix & breadcrumbs ----
  const kindPrefix = event.kind === 'experience'
    ? '/ex'
    : (event.kind === 'reservation' ? '/res' : '/events');
  const kindLabel = event.kind === 'experience'
    ? 'Experiences'
    : (event.kind === 'reservation' ? 'Reservations' : 'Events');
  const breadcrumbs = breadcrumbLD([
    { name: 'Home', url: '/' },
    { name: kindLabel, url: kindPrefix },
    { name: cityDisplay, url: `${kindPrefix}/${evCity}` },
    { name: event.name, url: `${kindPrefix}/${evCity}/${slug}` },
  ]);
  // Kind-aware structured data: experiences emit a Google Product + Tourist
  // Attraction graph (eligible for Shopping rich results + Things-To-Do
  // panels); reservations emit a Restaurant graph with ReserveAction;
  // classic events keep the legacy Event LD. We merge in `_reviews` so
  // the helpers can attach `aggregateRating` + `review[]` (Google star
  // chip) when buyer feedback exists.
  const eventForLD = reviewsData ? { ...event, _reviews: reviewsData } : event;
  const seoLD = event.kind === 'experience'
    ? [experienceProductLD(eventForLD), breadcrumbs]
    : event.kind === 'reservation'
      ? [reservationVenueLD(eventForLD), breadcrumbs]
      : [eventLD(event), breadcrumbs];

  return (
    <div className="min-h-screen bg-background pb-16">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={event.image_url || event.hero_image_url || allImages[0]}
        type="event"
        structuredData={seoLD}
      />
      {/* Promo partner banner */}
      {activePromoCode && (
        <div className="bg-gradient-to-r from-emerald-600 to-indigo-600 text-white text-center py-2 px-4 text-sm" data-testid="promo-banner">
          <span className="font-medium">🎟️ You arrived via a partner&apos;s promo link</span>
          <span className="opacity-80 hidden sm:inline"> — your purchase supports them at no extra cost.</span>
        </div>
      )}
      {/* Hero gallery — Bento on desktop, swipeable 4:5 carousel on mobile.
          See `/app/design_guidelines.json` for the layout spec we followed. */}
      <div className="pt-4 sm:pt-6">
        <HeroGallery
          images={allImages}
          eventName={event.name}
          onOpenLightbox={(i) => setLightboxIdx(i)}
        />
        <HeroLightbox
          images={allImages}
          openIdx={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 lg:mt-10">
        {/* Breadcrumbs — compact, lives above the title block */}
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4 flex-wrap" data-testid="event-breadcrumbs">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to={kindPrefix} className="hover:text-foreground">{kindLabel}</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to={`${kindPrefix}/${evCity}`} className="hover:text-foreground">{cityDisplay}</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground truncate">{event.name}</span>
        </nav>

        {/* Title block — inline meta line under H1 replaces the chip
            ribbon. Plain text, comma-separated facts, no button chrome.
            Tagline follows. Tags moved to the bottom of the page. */}
        <div className="flex items-start justify-between gap-4 mb-2" data-testid="event-title-block">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-indigo-300" data-testid="event-category-labels">
                {((event.categories && event.categories.length ? event.categories : [event.category])
                  .filter(Boolean)
                  .map((c) => CATEGORY_LABELS[c] || c)
                  .join(' · '))}
              </span>
              {event.is_featured && (
                <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px] uppercase tracking-widest">Featured</Badge>
              )}
              {event.status === 'cancelled' && (
                <Badge variant="destructive" className="text-[10px]">Cancelled</Badge>
              )}
            </div>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Space_Grotesk'] tracking-tight leading-[1.05]"
              data-testid="event-title"
            >
              {event.name}
            </h1>

            {/* Inline meta — plain prose, separated by middots. Mirrors
                DesignMyNight's information density at the top of the
                page. The venue is the only interactive element. */}
            <p className="text-sm sm:text-base text-foreground/90 mt-3 leading-relaxed" data-testid="event-inline-meta">
              {event.event_date && (
                <>
                  {formatDate(event.event_date)}
                  {event.event_time ? ` · ${formatTime(event.event_time)}` : ''}
                </>
              )}
              {event.venue_name && (
                <>
                  <span className="text-muted-foreground mx-2">·</span>
                  {event.venue_slug ? (
                    <Link
                      to={`/${city}/venues/${event.venue_slug}`}
                      className="font-semibold text-foreground hover:text-fuchsia-300 underline-offset-4 hover:underline"
                      data-testid="venue-profile-link"
                    >{event.venue_name}</Link>
                  ) : (
                    <span className="font-semibold">{event.venue_name}</span>
                  )}
                </>
              )}
              {priceRangeLabel(event) && (
                <>
                  <span className="text-muted-foreground mx-2">·</span>
                  <span className="font-semibold tabular-nums">{priceRangeLabel(event)}</span>
                </>
              )}
              {event.age_restriction && (
                <>
                  <span className="text-muted-foreground mx-2">·</span>
                  <span className="text-muted-foreground">{event.age_restriction}</span>
                </>
              )}
            </p>

            {event.tagline && (
              <p className="text-base sm:text-lg text-muted-foreground mt-3 max-w-3xl leading-relaxed">
                {event.tagline}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={handleShare}
            className="flex-shrink-0"
            data-testid="share-event-button"
            aria-label="Share event"
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Urgency strip — countdown + scarcity directly below the meta. */}
        {(countdownLabel(event.sale_end) || event.badge) && (
          <div
            className="flex items-center gap-2 flex-wrap mt-4"
            data-testid="event-urgency-strip"
          >
            {event.badge && (
              <Badge className={`${
                event.badge.color === 'red' ? 'bg-red-500'
                : event.badge.color === 'orange' ? 'bg-orange-500'
                : event.badge.color === 'purple' ? 'bg-purple-500'
                : 'bg-amber-500'
              } text-white text-[10px] font-bold border-0 uppercase tracking-widest`}>
                {event.badge.text}
              </Badge>
            )}
            {countdownLabel(event.sale_end) && (
              <span className="text-xs font-semibold text-amber-300 inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 animate-pulse" />
                {countdownLabel(event.sale_end)} to buy
              </span>
            )}
          </div>
        )}

        {/* Sticky tab nav + active section. On desktop we use an 8/4
            grid so the booking widget can sit in a sticky right
            sidebar, always visible. On mobile this is a single
            column and the booking widget lives inside the Tickets
            tab body (with a sticky bottom CTA bar pinned to the viewport). */}
        <div className="lg:grid lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8 min-w-0">
        <EventTabSections
          event={event}
          city={evCity}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabNavRef={tabNavRef}
          isDesktop={isDesktop}
          // Existing ticket widget logic (SlotPicker / ReservationPicker
          // / internal selection / external / embed) is passed in as a
          // render-prop so we don't have to copy ~250 lines of JSX into
          // a sub-component.
          renderTicketsTab={() => (
            <div id="event-ticket-widget" data-testid="event-tickets-tab-content" className="space-y-4">
              {/* Urgency band re-shown inside the Tickets tab so the
                  user always sees scarcity right next to the buy button. */}
              {(countdownLabel(event.sale_end) || event.badge) && (
                <div
                  className="rounded-2xl p-4 border border-amber-500/30 bg-gradient-to-r from-amber-500/15 via-rose-500/10 to-transparent"
                  data-testid="event-urgency-stack"
                >
                  {event.badge && (
                    <Badge
                      className={`${
                        event.badge.color === 'red' ? 'bg-red-500'
                        : event.badge.color === 'orange' ? 'bg-orange-500'
                        : event.badge.color === 'purple' ? 'bg-purple-500'
                        : 'bg-amber-500'
                      } text-white text-[10px] font-bold border-0 uppercase tracking-widest`}
                    >{event.badge.text}</Badge>
                  )}
                  {countdownLabel(event.sale_end) && (
                    <p className="text-sm font-bold text-amber-200 mt-2 flex items-center gap-1.5">
                      <Clock className="h-4 w-4 animate-pulse" />
                      {countdownLabel(event.sale_end)} to buy
                    </p>
                  )}
                </div>
              )}
              <Card
                className="border-fuchsia-500/20 shadow-2xl shadow-black/30"
                data-testid="buy-tickets-cta-button"
              >
                <CardContent className="pt-6">
                {/* Experiences & Reservations get dedicated booking widgets
                    instead of the standard ticket selector. */}
                {event.kind === 'experience' && (
                  <div className="space-y-3" data-testid="experience-booking">
                    <h3 className="font-heading text-lg font-bold text-center">Book your spot</h3>
                    <SlotPicker event={event} />
                  </div>
                )}
                {event.kind === 'reservation' && (
                  <div className="space-y-3" data-testid="reservation-booking">
                    <h3 className="font-heading text-lg font-bold text-center">Reserve a table</h3>
                    <ReservationPicker event={event} />
                  </div>
                )}
                {/* Internal Ticketing (kind defaults to "event") */}
                {(!event.kind || event.kind === 'event') && event.ticket_type === 'internal' && ticketTypes.length > 0 && !showCheckout && (
                  <div className="space-y-3" data-testid="ticket-selection">
                    <h3 className="font-heading text-lg font-bold text-center">Get Tickets</h3>

                    {/* Multi-day occurrence selector */}
                    {(event.occurrences || []).length > 0 && (
                      <div className="space-y-2 p-3 rounded-lg border border-indigo-500/30 bg-indigo-500/5" data-testid="occurrence-picker">
                        <p className="text-xs font-semibold text-indigo-300 uppercase tracking-wide">Pick your day</p>
                        <div className="grid gap-2">
                          {event.occurrences.filter(o => o.is_active !== false).map((occ) => {
                            const active = selectedOccurrenceId === occ.occurrence_id;
                            const av = occurrenceAvailability[occ.occurrence_id] || {};
                            const soldOut = !!av.is_sold_out;
                            const remaining = av.remaining;
                            return (
                              <button
                                key={occ.occurrence_id}
                                type="button"
                                disabled={soldOut}
                                onClick={() => { if (soldOut) return; setSelectedOccurrenceId(occ.occurrence_id); setTicketSelections({}); }}
                                className={`text-left p-2.5 rounded-md border transition-all ${soldOut ? 'opacity-50 cursor-not-allowed border-rose-500/30 bg-rose-500/5' : active ? 'border-indigo-500 bg-indigo-500/10' : 'border-border hover:border-indigo-500/40'}`}
                                data-testid={`occurrence-${occ.occurrence_id}`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="text-sm font-semibold flex items-center gap-2">
                                      <span>{occ.date}{occ.label ? ` · ${occ.label}` : ''}</span>
                                      {soldOut && <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30 text-[9px]" data-testid={`sold-out-${occ.occurrence_id}`}>Sold out</Badge>}
                                      {!soldOut && typeof remaining === 'number' && remaining > 0 && remaining <= 10 && <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[9px]">{remaining} left</Badge>}
                                    </p>
                                    <p className="text-[10px] text-muted-foreground">
                                      {occ.start_time && occ.end_time ? `${occ.start_time} – ${occ.end_time}` : occ.start_time || ''}
                                      {occ.restriction && ` · ${occ.restriction}`}
                                    </p>
                                  </div>
                                  {active && !soldOut && <CheckCircle2 className="h-4 w-4 text-indigo-400 flex-shrink-0" />}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {regularTypes.length > 0 && regularTypes.every(tt => (tt.quantity_total - (tt.quantity_sold || 0)) <= 0) && (
                      <WaitlistSignup eventId={event.event_id} orgSlug={event.organization_slug} />
                    )}
                    {regularTypes
                      .filter(tt => {
                        // If no occurrences, show all. If picked, show only matching + universal (occurrence_id null)
                        if (!(event.occurrences || []).length) return true;
                        if (!selectedOccurrenceId) return !tt.occurrence_id;
                        return !tt.occurrence_id || tt.occurrence_id === selectedOccurrenceId;
                      })
                      .map(tt => {
                      const available = tt.quantity_total - (tt.quantity_sold || 0);
                      const soldOut = available <= 0;
                      const qty = ticketSelections[tt.ticket_type_id] || 0;
                      return (
                        <div key={tt.ticket_type_id} className={`p-3 rounded-lg border ${soldOut ? 'opacity-50 border-border' : 'border-border hover:border-indigo-500/30'} transition-all`} data-testid={`ticket-type-${tt.ticket_type_id}`}>
                          <div className="flex items-start justify-between mb-1">
                            <div>
                              <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                                <p className="font-semibold text-sm">{tt.name}</p>
                                {tt.manual_badge && <TicketBadge badge={tt.manual_badge} size="xs" testId={`tt-manual-badge-${tt.ticket_type_id}`} />}
                                {tt.auto_badge && <TicketBadge badge={tt.auto_badge} size="xs" testId={`tt-auto-badge-${tt.ticket_type_id}`} />}
                              </div>
                              <Badge variant="outline" className="text-[9px] capitalize">{tt.tier}</Badge>
                            </div>
                            <p className="font-bold text-lg text-indigo-500">{tt.price === 0 ? 'Free' : `$${tt.price}`}</p>
                          </div>
                          {tt.description && <p className="text-xs text-muted-foreground mb-2">{tt.description}</p>}
                          {tt.perks?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {tt.perks.map((p, i) => <Badge key={i} variant="outline" className="text-[9px]"><CheckCircle2 className="h-2.5 w-2.5 mr-0.5 text-emerald-500" />{p}</Badge>)}
                            </div>
                          )}
                          {soldOut ? (
                            <Badge className="bg-red-500/20 text-red-400 text-xs w-full justify-center py-1">Sold Out</Badge>
                          ) : (
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-muted-foreground">
                                {tt.show_remaining === false ? (
                                  (tt.min_per_order || 1) > 1 ? `min ${tt.min_per_order} per order` : 'Available'
                                ) : (
                                  <>
                                    {available} remaining
                                    {(tt.min_per_order || 1) > 1 && <> · min {tt.min_per_order}</>}
                                  </>
                                )}
                              </span>
                              <div className="flex items-center gap-2">
                                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateTicketQty(tt.ticket_type_id, -1)} disabled={qty === 0}><Minus className="h-3 w-3" /></Button>
                                <span className="w-6 text-center font-semibold text-sm">{qty}</span>
                                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateTicketQty(tt.ticket_type_id, 1)} disabled={qty >= Math.min(tt.max_per_order || 10, available)}><Plus className="h-3 w-3" /></Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Add-ons — appear once at least one ticket is picked */}
                    {addonRows.length > 0 && totalTickets > 0 && (
                      <div className="space-y-2.5 pt-3 mt-1 border-t border-dashed border-fuchsia-500/40" data-testid="addons-section">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-md bg-fuchsia-500/15">
                            <Gift className="h-3.5 w-3.5 text-fuchsia-400" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold leading-tight">Enhance your experience</p>
                            <p className="text-[11px] text-muted-foreground">Optional extras — grab one for every ticket</p>
                          </div>
                        </div>
                        {addonRows.map(row => {
                          const available = (row.tt.quantity_total || 0) - (row.tt.quantity_sold || 0);
                          const soldOut = available <= 0;
                          const qty = addonSelections[row.key] || 0;
                          const label = row.variation ? `${row.tt.name} — ${row.variation.name}` : row.tt.name;
                          const allowed = row.tt.applies_to_ticket_types || [];
                          const allowedNames = allowed.map(id => regularTypes.find(t => t.ticket_type_id === id)?.name).filter(Boolean);
                          const locked = allowed.length > 0 && addonEligibleTickets(row.tt) === 0;
                          return (
                            <div key={row.key} className={`p-3 rounded-lg border border-dashed ${soldOut || locked ? 'opacity-50 border-border' : qty > 0 ? 'border-fuchsia-500/70 bg-gradient-to-r from-fuchsia-500/10 to-indigo-500/5' : 'border-fuchsia-500/30 bg-fuchsia-500/[0.03] hover:border-fuchsia-500/60'} transition-all`} data-testid={`addon-row-${row.key}`}>
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0 flex items-start gap-2.5">
                                  <Gift className={`h-4 w-4 mt-0.5 flex-shrink-0 ${qty > 0 ? 'text-fuchsia-400' : 'text-fuchsia-500/50'}`} />
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="font-semibold text-sm">{label}</p>
                                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-fuchsia-500/15 text-fuchsia-400">Add-on</span>
                                    </div>
                                    {row.tt.description && !row.variation && <p className="text-xs text-muted-foreground line-clamp-2">{row.tt.description}</p>}
                                    {allowedNames.length > 0 && <p className="text-[10px] text-fuchsia-400/80 mt-0.5" data-testid={`addon-applies-${row.key}`}>Available with: {allowedNames.join(', ')}</p>}
                                  </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  <p className="font-bold text-fuchsia-400">{row.price === 0 ? 'Free' : `+$${row.price}`}</p>
                                  {soldOut ? (
                                    <Badge className="bg-red-500/20 text-red-400 text-xs">Sold Out</Badge>
                                  ) : locked ? (
                                    <span className="text-[10px] text-muted-foreground font-medium whitespace-nowrap" data-testid={`addon-locked-${row.key}`}>Requires {allowedNames.join(' or ')}</span>
                                  ) : qty === 0 ? (
                                    <Button size="sm" variant="outline" className="h-7 px-3 text-xs border-fuchsia-500/50 text-fuchsia-400 hover:bg-fuchsia-500/10 hover:text-fuchsia-300 rounded-full" onClick={() => updateAddonQty(row, 1)} data-testid={`addon-plus-${row.key}`}>
                                      <Plus className="h-3 w-3 mr-1" /> Add
                                    </Button>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <Button size="icon" variant="outline" className="h-7 w-7 rounded-full border-fuchsia-500/40" onClick={() => updateAddonQty(row, -1)} data-testid={`addon-minus-${row.key}`}><Minus className="h-3 w-3" /></Button>
                                      <span className="w-6 text-center font-semibold text-sm">{qty}</span>
                                      <Button size="icon" variant="outline" className="h-7 w-7 rounded-full border-fuchsia-500/40" onClick={() => updateAddonQty(row, 1)} data-testid={`addon-plus-${row.key}`}><Plus className="h-3 w-3" /></Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {totalTickets > 0 && (
                      <div className="pt-2 border-t border-border">
                        <div className="flex justify-between text-sm mb-3">
                          <span className="text-muted-foreground">
                            {totalTickets} ticket{totalTickets > 1 ? 's' : ''}
                            {totalAddonUnits > 0 ? ` + ${totalAddonUnits} add-on${totalAddonUnits > 1 ? 's' : ''}` : ''}
                            {buyerFee > 0 ? ' + fees' : ''}
                          </span>
                          <span className="font-bold text-lg">{buyerTotal === 0 ? 'Free' : `$${buyerTotal.toFixed(2)}`}</span>
                        </div>
                        <Button className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700" size="lg" onClick={() => setShowCheckout(true)} data-testid="proceed-to-checkout">
                          <ShoppingCart className="mr-2 h-5 w-5" /> {buyerTotal === 0 ? 'RSVP Now' : 'Checkout'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Checkout Form */}
                {(!event.kind || event.kind === 'event') && event.ticket_type === 'internal' && showCheckout && (
                  <div className="space-y-3" data-testid="checkout-form">
                    <div className="flex items-center gap-2 mb-2">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setShowCheckout(false)}><ArrowLeft className="h-4 w-4" /></Button>
                      <h3 className="font-heading text-lg font-bold">Your Information</h3>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Full Name *</Label>
                      <Input value={buyerInfo.name} onChange={e => setBuyerInfo(p => ({ ...p, name: e.target.value }))} placeholder="John Doe" data-testid="buyer-name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Email *</Label>
                      <Input type="email" value={buyerInfo.email} onChange={e => setBuyerInfo(p => ({ ...p, email: e.target.value }))} placeholder="john@example.com" data-testid="buyer-email" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Phone</Label>
                      <Input value={buyerInfo.phone} onChange={e => setBuyerInfo(p => ({ ...p, phone: e.target.value }))} placeholder="(555) 123-4567" data-testid="buyer-phone" />
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex gap-2">
                        <Input value={discountCode} onChange={e => setDiscountCode(e.target.value.toUpperCase())} placeholder="Discount code" className="h-9 text-sm" data-testid="discount-code-field" />
                        <Button type="button" variant="outline" className="h-9 text-xs" onClick={() => applyDiscountCode()} disabled={discountChecking || !discountCode} data-testid="apply-discount-btn">
                          {discountChecking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                      {discountInfo && (
                        <p className="text-[11px] text-emerald-400 font-medium" data-testid="discount-applied-note">
                          Code {discountInfo.code} applied — {discountInfo.label}
                          <button type="button" className="ml-2 underline text-muted-foreground" onClick={() => { setDiscountInfo(null); setDiscountCode(''); }}>remove</button>
                        </p>
                      )}
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg text-xs space-y-1" data-testid="order-summary">
                      <p className="font-semibold text-sm mb-1">Order Summary</p>
                      {ticketTypes.filter(tt => (ticketSelections[tt.ticket_type_id] || 0) > 0).map(tt => (
                        <div key={tt.ticket_type_id} className="flex justify-between">
                          <span>{tt.name} x {ticketSelections[tt.ticket_type_id]}</span>
                          <span className="font-medium">{tt.price === 0 ? 'Free' : `$${(tt.price * ticketSelections[tt.ticket_type_id]).toFixed(2)}`}</span>
                        </div>
                      ))}
                      {addonRows.filter(row => (addonSelections[row.key] || 0) > 0).map(row => (
                        <div key={row.key} className="flex justify-between" data-testid={`summary-addon-${row.key}`}>
                          <span className="flex items-center gap-1"><Gift className="h-3 w-3 text-fuchsia-400" />{row.variation ? `${row.tt.name} — ${row.variation.name}` : row.tt.name} x {addonSelections[row.key]}</span>
                          <span className="font-medium">{row.price === 0 ? 'Free' : `$${(row.price * addonSelections[row.key]).toFixed(2)}`}</span>
                        </div>
                      ))}
                      {discountAmount > 0 && (
                        <>
                          <div className="flex justify-between pt-1 border-t border-border mt-1">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="font-medium">${totalPrice.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-emerald-400" data-testid="checkout-discount-line">
                            <span>Discount ({discountInfo?.code})</span>
                            <span className="font-medium">-${discountAmount.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      {buyerFee > 0 && (
                        <>
                          {discountAmount === 0 && (
                            <div className="flex justify-between pt-1 border-t border-border mt-1">
                              <span className="text-muted-foreground">Subtotal</span>
                              <span className="font-medium">${totalPrice.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between" data-testid="checkout-service-fee">
                            <span className="text-muted-foreground">Service fee</span>
                            <span className="font-medium">${buyerFee.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between font-bold text-sm pt-1 border-t border-border mt-1">
                        <span>Total</span>
                        <span data-testid="checkout-total">{buyerTotal === 0 ? 'Free' : `$${buyerTotal.toFixed(2)}`}</span>
                      </div>
                      {walletBalanceCents > 0 && buyerTotal > 0 && (
                        <>
                          <label className="flex items-center justify-between gap-2 pt-1 cursor-pointer select-none" data-testid="wallet-credit-row">
                            <span className="flex items-center gap-1.5 text-emerald-400">
                              <input type="checkbox" className="accent-emerald-500" checked={useWalletCredit}
                                onChange={e => setUseWalletCredit(e.target.checked)} data-testid="wallet-credit-checkbox" />
                              <Wallet className="h-3 w-3" /> Use wallet credit (${(walletBalanceCents / 100).toFixed(2)} available)
                            </span>
                            {useWalletCredit && <span className="font-medium text-emerald-400" data-testid="wallet-credit-amount">-${walletApplied.toFixed(2)}</span>}
                          </label>
                          {useWalletCredit && (
                            <div className="flex justify-between font-bold text-sm pt-1 border-t border-border mt-1">
                              <span>You pay</span>
                              <span data-testid="checkout-due">{buyerDue === 0 ? '$0.00 — covered by wallet' : `$${buyerDue.toFixed(2)}`}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <Button className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700" size="lg" onClick={handlePurchase} disabled={purchasing} data-testid="complete-purchase">
                      {purchasing ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Ticket className="mr-2 h-5 w-5" />}
                      {buyerTotal === 0 ? 'Confirm RSVP' : (useWalletCredit && buyerDue === 0 ? 'Pay with wallet' : `Pay $${(useWalletCredit ? buyerDue : buyerTotal).toFixed(2)}`)}
                    </Button>
                    <p className="text-[10px] text-muted-foreground text-center">Tickets will be emailed to you immediately after purchase</p>
                  </div>
                )}

                {/* Internal but no ticket types yet */}
                {(!event.kind || event.kind === 'event') && event.ticket_type === 'internal' && ticketTypes.length === 0 && (
                  <TicketsSoonNotify eventId={event.event_id} />
                )}

                {/* Legacy: price display */}
                {(!event.kind || event.kind === 'event') && event.ticket_type !== 'internal' && event.ticket_price_min && (
                  <div className="text-center mb-4">
                    <p className="text-sm text-muted-foreground">Starting from</p>
                    <p className="text-4xl font-bold text-indigo-500">
                      ${event.ticket_price_min}
                      {event.ticket_price_max && event.ticket_price_max !== event.ticket_price_min && (
                        <span className="text-lg text-muted-foreground"> - ${event.ticket_price_max}</span>
                      )}
                    </p>
                  </div>
                )}

                {(!event.kind || event.kind === 'event') && event.ticket_type === 'external' && event.ticket_url && (
                  <Button 
                    className="w-full bg-gradient-to-r from-fuchsia-500 via-amber-500 to-indigo-500 hover:opacity-90 text-white border-0 font-bold uppercase tracking-widest"
                    size="lg"
                    onClick={() => window.open(event.ticket_url, '_blank')}
                    data-testid="get-tickets-btn"
                  >
                    <Ticket className="mr-2 h-5 w-5" />
                    Get Tickets
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                )}

                {(!event.kind || event.kind === 'event') && event.ticket_type === 'embed' && event.ticket_embed && (
                  <div 
                    className="ticket-embed mt-4"
                    dangerouslySetInnerHTML={{ __html: event.ticket_embed }}
                  />
                )}
              </CardContent>
            </Card>
            </div>
          )}
        />
        {/* End of EventTabSections */}
          </div>{/* /lg:col-span-8 */}

          {/* Desktop sticky sidebar — always-visible booking panel. Only
              mounts on lg+ via JS `isDesktop` to prevent state drift with
              the mobile-tab instance. Same React tree as the Tickets tab
              body (the kind-aware experience / reservation / standard
              ticket branches) — extracted via the conditional below so
              we don't have to copy ~250 lines of JSX. */}
          {isDesktop && (
            <aside
              className="lg:col-span-4 min-w-0 lg:mt-0 mt-8"
              data-testid="event-sticky-sidebar"
            >
              <div className="sticky top-24 space-y-4">
                {/* Urgency band — stays in eyeline above the buy button. */}
                {(countdownLabel(event.sale_end) || event.badge) && (
                  <div
                    className="rounded-2xl p-4 border border-amber-500/30 bg-gradient-to-r from-amber-500/15 via-rose-500/10 to-transparent"
                  >
                    {event.badge && (
                      <Badge
                        className={`${
                          event.badge.color === 'red' ? 'bg-red-500'
                          : event.badge.color === 'orange' ? 'bg-orange-500'
                          : event.badge.color === 'purple' ? 'bg-purple-500'
                          : 'bg-amber-500'
                        } text-white text-[10px] font-bold border-0 uppercase tracking-widest`}
                      >{event.badge.text}</Badge>
                    )}
                    {countdownLabel(event.sale_end) && (
                      <p className="text-sm font-bold text-amber-200 mt-2 flex items-center gap-1.5">
                        <Clock className="h-4 w-4 animate-pulse" />
                        {countdownLabel(event.sale_end)} to buy
                      </p>
                    )}
                  </div>
                )}
                {/* Desktop booking widget — re-renders the experience /
                    reservation / standard-ticket branches. Self-contained
                    to keep the sidebar code review-friendly. The mobile
                    version lives inside the Tickets tab body (via
                    renderTicketsTab prop) and is JS-gated to one instance. */}
                <Card
                  className="border-fuchsia-500/20 shadow-2xl shadow-black/30"
                  data-testid="event-desktop-booking-card"
                >
                  <CardContent className="pt-6">
                    {event.kind === 'experience' && (
                      <div className="space-y-3" data-testid="experience-booking-desktop">
                        <h3 className="font-heading text-lg font-bold text-center">Book your spot</h3>
                        <SlotPicker event={event} />
                      </div>
                    )}
                    {event.kind === 'reservation' && (
                      <div className="space-y-3" data-testid="reservation-booking-desktop">
                        <h3 className="font-heading text-lg font-bold text-center">Reserve a table</h3>
                        <ReservationPicker event={event} />
                      </div>
                    )}
                    {(!event.kind || event.kind === 'event') && (
                      event.ticket_type === 'external' && event.ticket_url ? (
                        <div className="text-center space-y-3" data-testid="event-desktop-external-box">
                          <p className="text-sm text-muted-foreground uppercase tracking-widest font-bold">Tickets</p>
                          <p className="text-2xl font-bold font-['Space_Grotesk']">
                            {priceRangeLabel(event) || `On sale at ${externalSellerName(event.ticket_url)}`}
                          </p>
                          <Button
                            className="w-full bg-gradient-to-r from-fuchsia-500 via-amber-500 to-indigo-500 hover:opacity-90 text-white border-0 font-bold uppercase tracking-widest"
                            size="lg"
                            onClick={() => window.open(event.ticket_url, '_blank')}
                            data-testid="event-desktop-external-cta"
                          >
                            <Ticket className="mr-2 h-5 w-5" />
                            Get Tickets
                            <ExternalLink className="ml-2 h-4 w-4" />
                          </Button>
                          <p className="text-[11px] text-muted-foreground">
                            Ticket purchase completes on {externalSellerName(event.ticket_url)}
                          </p>
                        </div>
                      ) : event.ticket_type === 'internal' && ticketTypes.length === 0 ? (
                        <TicketsSoonNotify eventId={event.event_id} />
                      ) : (
                      <div className="text-center space-y-3">
                        <p className="text-sm text-muted-foreground uppercase tracking-widest font-bold">
                          {Number(event.ticket_price_min) === 0 ? 'Free entry' : 'Tickets from'}
                        </p>
                        <p className="text-4xl font-bold font-['Space_Grotesk']">
                          {Number(event.ticket_price_min) === 0
                            ? 'Free'
                            : `$${event.ticket_price_min ?? ''}`}
                          {Number(event.ticket_price_max) > Number(event.ticket_price_min) && (
                            <span className="text-base text-muted-foreground font-normal">
                              {' '}– ${event.ticket_price_max}
                            </span>
                          )}
                        </p>
                        <Button
                          className="w-full bg-gradient-to-r from-fuchsia-500 via-amber-500 to-indigo-500 hover:opacity-90 text-white border-0 font-bold uppercase tracking-widest"
                          size="lg"
                          onClick={switchToTickets}
                          data-testid="event-desktop-cta-jump"
                        >
                          <Ticket className="mr-2 h-5 w-5" />
                          {Number(event.ticket_price_min) === 0 ? 'RSVP now' : 'Get tickets'}
                        </Button>
                        <p className="text-[11px] text-muted-foreground">
                          {event.ticket_type === 'external'
                            ? 'Tickets handled by the organizer'
                            : 'Secure checkout · Eventism Pay'}
                        </p>
                      </div>
                      )
                    )}
                  </CardContent>
                </Card>
              </div>
            </aside>
          )}
        </div>{/* /lg:grid */}

        {/* Tags — moved to the bottom of the page per the IA pass. */}
        {event.tags?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-border" data-testid="event-tags-bottom">
            <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-3">Tagged</p>
            <div className="flex flex-wrap gap-1.5">
              {event.tags.map((tag) => (
                <Link key={tag} to={`/events/${evCity}/tags/${tag}`}>
                  <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80 text-xs">
                    <Tag className="h-3 w-3 mr-1" /> {tag}
                  </Badge>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* "More like this" — similar upcoming events. Lazy-fetched. */}
        <MoreLikeThisCarousel
          city={evCity}
          category={event.category}
          excludeId={event.event_id}
          kindPrefix={kindPrefix}
        />

        <LogoSoupRow eventId={event.event_id} />
      </div>

      {/* Sticky bottom CTA bar on mobile — always-visible "Get Tickets"
          conversion lever. Tap switches to the Tickets tab + scrolls. */}
      <MobileBottomCTA
        event={event}
        countdown={countdownLabel(event.sale_end)}
        onTapTickets={switchToTickets}
      />
    </div>
  );
}

/**
 * HtmlPageTakeover — replaces the entire event page with admin-authored HTML.
 * Honors three toggles on the block.data:
 *   - hide_global_header   → adds body class to hide <Navbar/>
 *   - hide_global_footer   → adds body class to hide <Footer/>
 *   - show_floating_ticket_button → renders a fixed right-side button
 */
function HtmlPageTakeover({ block, event }) {
  const data = block.data || {};
  const html = data.html || '';
  const showFloatBtn = !!data.show_floating_ticket_button;
  const btnLabel = data.ticket_button_label || 'Buy Tickets';
  const btnUrl = data.ticket_button_url || '#tickets';

  // Detect if the admin pasted a full HTML document (with <!DOCTYPE>, <html>, <head>,
  // or top-level <body>/<script src=...>). React's dangerouslySetInnerHTML strips
  // those root tags and — critically — never executes <script> tags. So when the
  // payload is a full doc, we render it in an iframe via `srcDoc` so the user's
  // Tailwind CDN, React/Babel CDNs, Eventbrite widgets, and inline scripts all
  // execute in their own clean document scope.
  const isFullDoc = /<!doctype\s+html|<html[\s>]|<head[\s>]|<body[\s>]/i.test(html) || /<script[\s>]/i.test(html);

  useEffect(() => {
    const body = document.body;
    body.classList.add('event-html-takeover');
    if (data.hide_global_header) body.classList.add('event-html-hide-nav');
    if (data.hide_global_footer) body.classList.add('event-html-hide-footer');
    if (isFullDoc) body.classList.add('event-html-iframe-mode');
    return () => {
      body.classList.remove('event-html-takeover');
      body.classList.remove('event-html-hide-nav');
      body.classList.remove('event-html-hide-footer');
      body.classList.remove('event-html-iframe-mode');
    };
  }, [data.hide_global_header, data.hide_global_footer, isFullDoc]);

  // For floating button URLs:
  //  - http(s)://...  → open in new tab (works in both modes)
  //  - #anchor       → smooth-scroll. In snippet mode we scroll the parent doc;
  //                    in iframe mode we postMessage into the iframe so the user's
  //                    page can scroll itself.
  const iframeRef = useRef(null);
  const handleFloatClick = (e) => {
    if (!btnUrl.startsWith('#')) return;
    e.preventDefault();
    if (isFullDoc) {
      const win = iframeRef.current?.contentWindow;
      try {
        // First try: scroll inside iframe directly (works because srcDoc is same-origin).
        const doc = iframeRef.current?.contentDocument;
        const target = doc?.querySelector(btnUrl);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      } catch { /* cross-origin fallback */ }
      // Fallback: postMessage so the user's HTML can listen and scroll itself.
      try { win?.postMessage({ type: 'eventism:scroll-to', anchor: btnUrl }, '*'); } catch { /* ignore */ }
    } else {
      const el = document.querySelector(btnUrl);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="event-html-page-root" data-testid="event-html-page-takeover">
      {isFullDoc ? (
        <iframe
          ref={iframeRef}
          title={event?.name || 'Event page'}
          srcDoc={html}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals"
          referrerPolicy="no-referrer"
          data-testid="event-html-page-iframe"
          style={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            border: 0,
            display: 'block',
            background: '#000',
            zIndex: 1,
          }}
        />
      ) : (
        <div
          className="event-html-page-content"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
      {showFloatBtn && (
        <a
          href={btnUrl}
          onClick={handleFloatClick}
          target={btnUrl.startsWith('http') ? '_blank' : undefined}
          rel={btnUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
          data-testid="floating-ticket-button"
          aria-label={btnLabel}
          style={{
            position: 'fixed',
            right: '24px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 9999,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '14px 22px',
            borderRadius: '999px',
            background: 'linear-gradient(135deg, #6366f1, #10b981)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '14px',
            textDecoration: 'none',
            boxShadow: '0 10px 30px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.1)',
            backdropFilter: 'blur(8px)',
            transition: 'transform 200ms ease, box-shadow 200ms ease',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(1.05)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
        >
          <Ticket style={{ width: 16, height: 16 }} />
          {btnLabel}
        </a>
      )}
    </div>
  );
}
